import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LayoutGrid, List, Network } from 'lucide-react';

import { MicrostoreElementState, type GetSiteQuery, TotadminPermission } from 'data/__generated__';

import { Button } from '@/components/ui/button';
import { SectionContainer } from 'components/DetailsView/Section';

type Site = NonNullable<GetSiteQuery['site']>;
type ColumnGroup = Site['microstoreColumnGroups'][number];
type Column = ColumnGroup['columns'][number];

type ParamTypes = {
    siteId: string;
};

type ViewMode = 'graphical' | 'list';

const STATE_COLOR: Record<MicrostoreElementState, string> = {
    [MicrostoreElementState.Regular]: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    [MicrostoreElementState.Disabled]: 'bg-amber-100 text-amber-900 border-amber-300',
    [MicrostoreElementState.Archived]: 'bg-zinc-100 text-zinc-600 border-zinc-300',
};

export const MicrostoreColumnsSection = ({ groups }: { groups: ColumnGroup[] }) => {
    const [view, setView] = useState<ViewMode>('graphical');
    const { siteId = '' } = useParams<ParamTypes>();

    const sortedGroups = [...groups].sort((a, b) => a.positionInSite - b.positionInSite);
    const totalColumns = sortedGroups.reduce((sum, g) => sum + g.columns.length, 0);

    return (
        <SectionContainer title="Microstore" isInitiallyOpen>
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                        {sortedGroups.length} groupe{sortedGroups.length > 1 ? 's' : ''} · {totalColumns} colonne
                        {totalColumns > 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center gap-2">
                        {siteId ? (
                            <Button
                                asChild
                                type="button"
                                size="xs"
                                variant="outline"
                                requiredPermissions={[TotadminPermission.MicrostoreColumnGroupsRead]}
                            >
                                <Link to={`/site/${siteId}/microstore-column-groups`}>
                                    <Network />
                                    Arborescence
                                </Link>
                            </Button>
                        ) : null}
                        <div className="flex gap-1">
                            <Button
                                type="button"
                                size="xs"
                                variant={view === 'graphical' ? 'default' : 'outline'}
                                onClick={() => setView('graphical')}
                                aria-pressed={view === 'graphical'}
                            >
                                <LayoutGrid />
                                Graphique
                            </Button>
                            <Button
                                type="button"
                                size="xs"
                                variant={view === 'list' ? 'default' : 'outline'}
                                onClick={() => setView('list')}
                                aria-pressed={view === 'list'}
                            >
                                <List />
                                Liste
                            </Button>
                        </div>
                    </div>
                </div>
                {sortedGroups.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Aucun groupe configuré sur ce site.</div>
                ) : view === 'graphical' ? (
                    <GraphicalView groups={sortedGroups} />
                ) : (
                    <ListView groups={sortedGroups} />
                )}
            </div>
        </SectionContainer>
    );
};

const GraphicalView = ({ groups }: { groups: ColumnGroup[] }) => (
    <div className="flex flex-wrap gap-3">
        {groups.map((group) => {
            const sortedColumns = [...group.columns].sort((a, b) => a.positionInColumnGroup - b.positionInColumnGroup);
            return (
                <div
                    key={group._id}
                    className="flex min-w-56 flex-col gap-1.5 rounded-lg border border-border bg-card p-2"
                >
                    <Link
                        to={`/microstore-column-group/${group._id}`}
                        className="text-xs font-semibold text-foreground hover:underline"
                    >
                        {group.name}
                    </Link>
                    <div className="flex flex-wrap gap-2">
                        {sortedColumns.length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                            sortedColumns.map((col) => <ColumnChip key={col._id} column={col} />)
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

const ColumnChip = ({ column }: { column: Column }) => (
    <Link
        to={`/microstore-column/${column._id}`}
        title={`${column.name} · ${column.microstoreColumnTemplateName}${column.isPaused ? ' (en pause)' : ''}`}
        className={`flex h-24 w-24 flex-col items-center justify-center gap-0.5 rounded-md border text-xs font-medium leading-tight transition-transform hover:-translate-y-0.5 hover:shadow-sm ${STATE_COLOR[column.state]}`}
    >
        <span className="text-xl font-semibold">{column.positionInColumnGroup + 1}</span>
        <span className="line-clamp-2 w-full px-1 text-center">{column.name}</span>
    </Link>
);

const ListView = ({ groups }: { groups: ColumnGroup[] }) => (
    <div className="flex flex-col divide-y divide-border rounded-md border border-border">
        {groups.map((group) => {
            const sortedColumns = [...group.columns].sort((a, b) => a.positionInColumnGroup - b.positionInColumnGroup);
            return (
                <div key={group._id} className="flex flex-col gap-1 p-2">
                    <div className="flex items-center justify-between">
                        <Link
                            to={`/microstore-column-group/${group._id}`}
                            className="text-sm font-semibold hover:underline"
                        >
                            {group.name}
                        </Link>
                        <span className="text-xs text-muted-foreground">
                            {sortedColumns.length} colonne{sortedColumns.length > 1 ? 's' : ''}
                        </span>
                    </div>
                    {sortedColumns.length > 0 ? (
                        <ul className="flex flex-col divide-y divide-border/60">
                            {sortedColumns.map((col) => (
                                <li key={col._id} className="flex items-center justify-between py-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 text-xs font-mono text-muted-foreground">
                                            #{col.positionInColumnGroup + 1}
                                        </span>
                                        <Link to={`/microstore-column/${col._id}`} className="text-sm hover:underline">
                                            {col.name}
                                        </Link>
                                        <span className="text-xs text-muted-foreground">
                                            {col.microstoreColumnTemplateName}
                                        </span>
                                    </div>
                                    <span
                                        className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATE_COLOR[col.state]}`}
                                    >
                                        {col.state}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            );
        })}
    </div>
);
